import { Link } from 'react-router-dom';
import { PayButton } from './PayButton';
import { Notice, StatusPill } from './ui';
import { formatMoney } from '../lib/format';
import type { Payment } from '../types/workspace';
import { IconLock } from './NavIcons';

/** Full-page Rated step experience while the rating fee is unpaid. */
export default function RatingFeePage({
  fee,
  published,
  waitingMessage,
  onPaid,
}: {
  fee: Payment | null;
  published: boolean;
  waitingMessage?: string;
  onPaid: () => void;
}) {
  return (
    <div className="mt-6 space-y-5">
      <section
        className="mz-fade-up overflow-hidden rounded-3xl"
        style={{
          border: '1px solid #f9a8d4',
          background: 'linear-gradient(145deg, #7a0038 0%, #c4005e 45%, #ff6aad 100%)',
          boxShadow: '0 22px 48px rgba(214,0,110,0.28)',
        }}
      >
        <div className="grid gap-0 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="p-6 text-white sm:p-10">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                <IconLock className="h-6 w-6" />
              </span>
              <StatusPill label="Step 3 · Rated" tone="pink" />
            </div>
            <h2 className="mt-5 text-3xl font-black tracking-tight sm:text-4xl">
              {published ? 'Your rating is ready' : 'Secure your rating unlock'}
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85">
              {published
                ? 'Your Micus analyst has published your credit rating. Complete the rating fee to open the full report, then update documents and submit again if you want a refreshed review.'
                : 'Pay the rating fee now so your result unlocks the moment your analyst publishes it — nothing waits on you.'}
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-white/90">
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
                View your grade, score and analyst summary
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
                Replace any documents and submit for another review
              </li>
              <li className="flex gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
                Continue to Ready once you are happy with the rating
              </li>
            </ul>
          </div>

          <div className="bg-white p-6 sm:p-10">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Rating fee</p>
            {fee ? (
              <>
                <p className="mt-2 text-4xl font-black tracking-tight text-[#d6006e]">{formatMoney(fee.amount, fee.currency)}</p>
                <p className="mt-2 text-sm text-[#6b3a52]">
                  {fee.mode === 'DOMESTIC' ? 'UPI, cards, netbanking & wallets' : 'International card payment'} · secured by Razorpay
                </p>
                <div className="mt-6">
                  <PayButton payment={fee} onPaid={onPaid} />
                </div>
              </>
            ) : (
              <p className="mt-3 text-sm text-[#6b3a52]">Your rating fee will appear here once documents are submitted.</p>
            )}
            <Link to="/payments" className="mt-4 inline-block text-xs font-bold text-[#d6006e]">
              View all payments →
            </Link>
          </div>
        </div>
      </section>

      {!published && waitingMessage && <Notice tone="pink">{waitingMessage}</Notice>}
    </div>
  );
}
